/**
 * ConvertPanel (ADR-080 WS5) — the "Convert a skill" studio flow: pick an
 * agent + skill, get a reviewable decomposition PLAN (WS3), edit it, MATERIALIZE
 * the leaner skill + N workflows (each DSL-validated), then PROPOSE the whole
 * set as one in-app review (WS4). Approvals stay in Mission Control (no GitHub).
 *
 * Extracted from Workflows.tsx and hosted under the Skills view (it operates on
 * a skill), so all skill authoring lives in one place.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, AuthorTestsFailingError, type AgentSkills, type ConvertResult, type FailingCheck, type ProposeConversionResult } from '../api/client'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { PendingReferences } from '../components/PendingReferences'

export function ConvertPanel({
  canPropose,
  onClose,
}: {
  canPropose: boolean
  onClose: () => void
}) {
  const [skillMap, setSkillMap] = useState<AgentSkills[]>([])
  const [skillsLoading, setSkillsLoading] = useState(true)
  const [skillsErr, setSkillsErr] = useState<string | null>(null)
  const [agent, setAgent] = useState('')
  const [skill, setSkill] = useState('') // the selected skill's slug
  const [phase, setPhase] = useState<'idle' | 'busy' | 'plan' | 'draft' | 'done'>('idle')
  const [matSecs, setMatSecs] = useState(0) // elapsed seconds during the materialize poll
  const [planText, setPlanText] = useState('')
  const [materialized, setMaterialized] = useState<ConvertResult | null>(null)
  const [proposed, setProposed] = useState<ProposeConversionResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  // ADR-107 P3: the fleet's own author tests failed against this proposed
  // set (409 author_tests_failing) — rendered via the SAME FailingChecksPanel
  // ADR-1027's PR review uses, not the generic error text.
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [helpOpen, setHelpOpen] = useState(true)
  const em = (e: unknown) => (e instanceof Error ? e.message : String(e))

  // Load agents-with-skills (NOT agents-with-activities — conversion operates on
  // skills, and the agents worth converting typically have no workflows yet).
  // ADR-103: exclude core-owned agents/skills entirely — Convert is purely an
  // authoring flow, with no read-only browse mode, so there's nothing to show
  // for a target that can never be proposed.
  useEffect(() => {
    let live = true
    api.listAgentSkills()
      .then(r => {
        if (!live) return
        const agentsWithSkills = (r.agents ?? [])
          .filter(a => !a.core)
          .map(a => ({ ...a, skills: a.skills.filter(s => !s.core) }))
          .filter(a => a.skills.length > 0)
        setSkillMap(agentsWithSkills)
        const first = agentsWithSkills[0]
        if (first) { setAgent(first.agent); setSkill(first.skills[0]?.slug ?? '') }
      })
      .catch(e => { if (live) setSkillsErr(em(e)) })
      .finally(() => { if (live) setSkillsLoading(false) })
    return () => { live = false }
  }, [])

  const currentSkills = useMemo(
    () => skillMap.find(a => a.agent === agent)?.skills ?? [],
    [skillMap, agent],
  )
  const selectedSkill = currentSkills.find(s => s.slug === skill)

  // Switching agent resets the skill to that agent's first (and clears any
  // in-progress plan/draft so the two never get out of sync).
  const onAgentChange = (next: string) => {
    setAgent(next)
    const skills = skillMap.find(a => a.agent === next)?.skills ?? []
    setSkill(skills[0]?.slug ?? '')
    setPhase('idle'); setPlanText(''); setMaterialized(null); setProposed(null); setError(null); setFailingChecks([])
  }
  const onSkillChange = (next: string) => {
    setSkill(next)
    setPhase('idle'); setPlanText(''); setMaterialized(null); setProposed(null); setError(null); setFailingChecks([])
  }

  const runPlan = async () => {
    setError(null); setFailingChecks([]); setProposed(null); setMaterialized(null); setPhase('busy')
    try {
      const r = await api.convertSkill(agent, skill, 'plan')
      // A null/empty plan means the copilot couldn't produce one (it burned its
      // attempts). Don't advance into an empty editor showing "{}" — say so, and
      // surface the model's last output if we have it, so the user can retry or
      // add a steer note.
      const plan = r.plan
      const empty = plan == null || (typeof plan === 'object' && Object.keys(plan as object).length === 0)
      if (empty) {
        setError(
          "The copilot couldn't produce a decomposition plan for this skill. Try again, or add a steer note." +
          (r.debug ? `\n\nModel output (excerpt):\n${r.debug}` : ''),
        )
        setPhase('idle')
        return
      }
      setPlanText(JSON.stringify(plan, null, 2))
      setPhase('plan')
    } catch (e) { setError(em(e)); setPhase('idle') }
  }
  const runMaterialize = async () => {
    // Materialize is a background job (POST -> job id -> poll) that can run for
    // several minutes; onProgress ticks each ~2s poll so the button shows live
    // elapsed time instead of a static "working" label.
    setError(null); setFailingChecks([]); setMatSecs(0); setPhase('busy')
    try {
      const r = await api.convertMaterialize(agent, skill, planText, undefined, () => setMatSecs(s => s + 2))
      setMaterialized(r); setPhase('draft')
    } catch (e) { setError(em(e)); setPhase('plan') }
  }
  const runPropose = async () => {
    if (!materialized?.skill) return
    setError(null); setFailingChecks([]); setPhase('busy')
    try {
      const r = await api.proposeConversion(
        agent,
        materialized.skill,
        (materialized.activities ?? []).map(a => ({ name: a.name, yaml: a.yaml })),
      )
      setProposed(r); setPhase('done')
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else setError(em(e))
      setPhase('draft')
    }
  }

  const allValid = !!materialized?.all_valid
  const busy = phase === 'busy'
  const canPlan = agent !== '' && skill !== '' && !busy

  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50/40 px-4 py-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-purple-800">Convert a skill → leaner skill + workflows</h3>
        <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-800">✕ close</button>
      </div>

      {/* In-app help: what conversion does + the 4 steps. Collapsible, on by default. */}
      <div className="rounded border border-purple-200 bg-white/70 text-xs text-gray-600">
        <button type="button" onClick={() => setHelpOpen(o => !o)}
          className="w-full flex items-center gap-1 px-2 py-1.5 font-medium text-purple-700 hover:text-purple-900">
          <span className="text-[9px] w-2">{helpOpen ? '▾' : '▸'}</span>
          How conversion works
        </button>
        {helpOpen && (
          <div className="px-3 pb-2 space-y-1">
            <p>Turn a prose <span className="font-medium">skill</span> into a leaner router skill plus deterministic <span className="font-medium">workflows</span> (validated dsl activities) — the recommended pattern for logic that should run reliably instead of being re-reasoned each time.</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li><span className="font-medium">Pick</span> an agent and one of its skills below.</li>
              <li><span className="font-medium">Draft a plan</span> — the copilot proposes a decomposition (no code yet); review and edit it.</li>
              <li><span className="font-medium">Materialize</span> — it writes the revised skill + workflow files, each DSL-validated (runs in the background; can take a few minutes).</li>
              <li><span className="font-medium">Propose</span> — send the whole set for approval in Mission Control{canPropose ? '' : ' (fleet owner only)'}. Nothing is written to the repo until it&apos;s approved.</li>
            </ol>
          </div>
        )}
      </div>

      {skillsLoading ? (
        <p className="text-xs text-gray-400 animate-pulse">Loading agents &amp; skills…</p>
      ) : skillsErr ? (
        <p className="text-xs text-red-600">Couldn&apos;t load skills: {skillsErr}</p>
      ) : skillMap.length === 0 ? (
        <p className="text-xs text-gray-500">No agent has any skills to convert yet. Author a skill first, then come back here.</p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-600">Agent
            <select value={agent} onChange={e => onAgentChange(e.target.value)}
              className="mt-1 block text-sm border border-gray-300 rounded px-2 py-1 min-w-[10rem]">
              {skillMap.map(a => (
                <option key={a.agent} value={a.agent}>{a.agent} ({a.skills.length})</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-600">Skill
            <select value={skill} onChange={e => onSkillChange(e.target.value)}
              className="mt-1 block text-sm border border-gray-300 rounded px-2 py-1 min-w-[12rem]">
              {currentSkills.map(s => (
                <option key={s.slug} value={s.slug}>{s.name !== s.slug ? `${s.name} (${s.slug})` : s.slug}</option>
              ))}
            </select>
          </label>
          <button type="button" disabled={!canPlan} onClick={() => { void runPlan() }}
            className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">
            {busy && !planText ? 'Analyzing…' : 'Draft a plan'}
          </button>
        </div>
      )}
      {/* Selected skill's description — visibility into what you're about to convert. */}
      {!skillsLoading && selectedSkill?.description && (
        <p className="text-xs text-gray-500 italic">{selectedSkill.description}</p>
      )}

      {error && <p className="text-xs text-red-600 whitespace-pre-wrap break-words max-h-48 overflow-auto">{error}</p>}
      {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}

      {phase === 'plan' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-600">Review/edit the decomposition plan, then materialize the files:</p>
          <textarea value={planText} onChange={e => setPlanText(e.target.value)} rows={10}
            className="w-full text-xs font-mono border border-gray-300 rounded px-2 py-1" />
          <button type="button" disabled={busy} onClick={() => { void runMaterialize() }}
            className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">
            {busy ? `Materializing… ${matSecs}s (this can take a few minutes)` : 'Materialize files'}
          </button>
        </div>
      )}

      {(phase === 'draft' || phase === 'busy') && materialized && (
        <div className="space-y-2">
          {materialized.skill && (
            <details className="text-xs">
              <summary className="cursor-pointer font-medium text-gray-700">Revised skill: {materialized.skill.name}</summary>
              <pre className="mt-1 whitespace-pre-wrap bg-white border border-gray-200 rounded p-2">{materialized.skill.content}</pre>
            </details>
          )}
          <div className="text-xs font-medium text-gray-700">Workflows ({materialized.activities?.length ?? 0}):</div>
          <ul className="space-y-1">
            {(materialized.activities ?? []).map(a => (
              <li key={a.name} className="text-xs">
                <span className={a.validation.ok ? 'text-green-700' : 'text-red-700'}>{a.validation.ok ? '✓' : '✗'}</span>{' '}
                <span className="font-mono">{a.name}</span>
                {!a.validation.ok && <span className="text-red-600"> — {a.validation.errors.join('; ')}</span>}
                <PendingReferences pending={a.validation.pending} className="text-xs ml-4" />
              </li>
            ))}
          </ul>
          {allValid ? (
            canPropose ? (
              <button type="button" disabled={busy} onClick={() => { void runPropose() }}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                {busy ? 'Running checks…' : `Propose (skill + ${materialized.activities?.length ?? 0} workflows)`}
              </button>
            ) : <p className="text-xs text-gray-500">Only a fleet owner can propose this conversion.</p>
          ) : (
            <p className="text-xs text-amber-700">Some workflows failed validation — edit the plan and re-materialize before proposing.</p>
          )}
        </div>
      )}

      {phase === 'done' && proposed && (
        proposed.improvement_warning ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
            <p>{proposed.improvement_warning}</p>
          </div>
        ) : (
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1">
            <p>Sent for review — a proposed change with {proposed.files.length} file{proposed.files.length === 1 ? '' : 's'} (the leaner skill + its workflows). Nothing ships until you approve it.</p>
            {proposed.improvement_id ? (
              <Link to={`/improvements/${encodeURIComponent(proposed.improvement_id)}/pr`} className="font-medium underline">
                Review &amp; approve in Mission Control →
              </Link>
            ) : (
              <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link> to approve &amp; deploy.</span>
            )}
          </div>
        )
      )}
    </div>
  )
}
